/**
 * Environment utilities for the application
 * Helps manage port detection and API URLs
 */

/**
 * Gets the current environment (development/production)
 * Using a consistent approach across the application
 */
export function getEnvironment() {
  // First check explicit environment variable
  const metaEnv = (() => {
    try {
      return (import.meta as any).env || {};
    } catch (_error) {
      return {} as Record<string, string>;
    }
  })();

  if (metaEnv.MODE) {
    return metaEnv.MODE;
  }
  
  // Then check if we're in a browser context
  if (typeof window !== 'undefined') {
    // Check hostname
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'development';
    }
    return 'production';
  }
  
  // Default to the NODE_ENV for server-side
  return metaEnv.NODE_ENV || 'development';
}

/**
 * Gets the API base URL based on the current environment
 * 
 * @returns The base URL to use for API requests
 */
export function getApiBaseUrl(): string {
  const metaEnv = (() => {
    try {
      return (import.meta as any).env || {};
    } catch (_error) {
      return {} as Record<string, string>;
    }
  })();

  // Prefer same-origin calls when running in the browser to avoid CORS.
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  if (metaEnv.VITE_API_URL) {
    return metaEnv.VITE_API_URL;
  }

  if (typeof process !== 'undefined') {
    if (process.env.API_BASE_URL) return process.env.API_BASE_URL;
    if (process.env.VITE_API_URL) return process.env.VITE_API_URL;
    if (process.env.NEXT_PUBLIC_API_URL) return process.env.NEXT_PUBLIC_API_URL;
  }

  const fallbackPort =
    (typeof process !== 'undefined' && process.env.VITE_BACKEND_PORT) ||
    metaEnv.VITE_BACKEND_PORT ||
    '3013';

  return `http://localhost:${fallbackPort}`;
}

/**
 * Gets the frontend base URL based on the current environment
 * 
 * @returns The base URL for frontend links
 */
export function getFrontendBaseUrl(): string {
  const metaEnv = (() => {
    try {
      return (import.meta as any).env || {};
    } catch (_error) {
      return {} as Record<string, string>;
    }
  })();

  // First check environment variables
  if (metaEnv.VITE_FRONTEND_URL) {
    return metaEnv.VITE_FRONTEND_URL;
  }
  
  // Production frontend URL is the same as the current domain
  const isProduction = getEnvironment() === 'production';
  
  if (isProduction && typeof window !== 'undefined') {
    return window.location.origin;
  }
  
  // In development, the frontend port might change (could be 3010, 3011, etc)
  // We'll use the current window location origin which should have the correct port
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  
  return 'http://localhost:3010';
}

/**
 * Gets the correct WebSocket URL for real-time features
 * 
 * @returns The WebSocket URL
 */
export function getWebSocketUrl(): string {
  // Similar logic to getApiBaseUrl but for WebSocket connections
  const isProduction = getEnvironment() === 'production';
  
  if (isProduction) {
    if (typeof window !== 'undefined') {
      // Use WebSockets on the same domain - NGINX will proxy /ws to the appropriate service
      const hostname = window.location.hostname;
      return `wss://${hostname}/ws`;
    }
    return 'wss://localhost/ws';
  }
  
  // Local development WebSocket URL
  return 'ws://localhost:3013/ws';
} 
